import { hasRight } from "@/components/crm/crmShared";
import DeskCloseX from "@/components/common/DeskCloseX";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { CheckCircle2, XCircle, Ban, Bell, Edit3, Clock, Info, Paperclip, Download, Lock } from "lucide-react";
import { getFileTypeMeta } from "@/lib/fileTypeMeta";
import AttachmentImagePreview from "@/components/common/AttachmentImagePreview";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { markSeen } from "@/lib/seen";
import { useAuth } from "@/context/AuthContext";
import { AVI_SUBTYPES } from "@/components/icons/AviSubtypeIcons";
import { AVI_STATUS_OVERRIDES } from "@/lib/aviSubtypePalette";
import { avatarColorForSeed } from "@/lib/avatarColors";
import UserAvatar from "@/components/ui/UserAvatar";
import AviDecisionModal from "@/components/avi/AviDecisionModal";
import AviReasonModal from "@/components/avi/AviReasonModal";
import TaskCreateDialog from "@/components/tasks/TaskCreateDialog";
import AviReminderModal from "@/components/avi/AviReminderModal";
import AviQuickEditModal from "@/components/avi/AviQuickEditModal";
import AviChat from "@/components/avi/AviChat";
import AviHistory from "@/components/avi/AviHistory";
import useAviStream from "@/hooks/useAviStream";
import useEscapeClose from "@/hooks/useEscapeClose";
import AviDetailBody from "@/components/avi/AviDetailBody";

/**
 * AviDetailModal — Itération 6 Phase 2 (rev-avi-6, Feb 2026).
 *
 * Fiche détail d'une demande AVI, présentée en MODALE fullscreen
 * centrée qui overlay la page liste (au lieu de l'ancienne page
 * `/avi/:id` autonome). Deep-linking préservé via l'URL `/avi/:id`.
 *
 * Règles :
 *   • Rôles-based actions : Décideur → Approuver/Refuser ;
 *     Demandeur/Admin → Modifier/Annuler tant que non-décidé.
 *   • Read-only PERMANENT dès qu'une décision finale est prise
 *     (approuvée / refusée / annulée). AVI n'a PAS de mécanisme
 *     de réouverture — contrairement aux Tâches et Documents.
 *   • Fermeture : backdrop click / Escape / bouton X → navigate('/avi').
 */

const PRIORITY_META = {
  immediat: { label: "Immédiat", color: "#EF4444" },
  urgent:   { label: "Urgent",   color: "#F97316" },
  moyen:    { label: "Moyen",    color: "#F9BC26" },
  normal:   { label: "Normal",   color: "#4ADE80" },
};

const STATUS_META = {
  en_attente:      { label: "En attente",           color: "#F9BC26" },
  approuvee:       { label: "Approuvée",            color: "#4ADE80" },
  accusee:         { label: "Reçu",                 color: "#F97316" },
  // Spec iter164k (2026-02-25) — palette verte cohérente Info (was #5aa8e0).
  lue:             { label: "Lue",                  color: "#2a9a7a" },
  refusee:         { label: "Refusée",              color: "#F87171" },
  annulee:         { label: "Annulée",              color: "#9CA3AF" },
  partiel_repondu: { label: "Partiellement répondu", color: "#F9BC26" },
};

// Spec iter164l (2026-02-25) — les surcharges de couleur par sous-type
// sont désormais centralisées dans /lib/aviSubtypePalette.js (import
// `AVI_STATUS_OVERRIDES`). Le bloc local INFO_STATUS_OVERRIDES a été
// retiré pour éviter les doublons.

const FINAL_STATUSES = ["approuvee", "refusee", "accusee", "lue", "annulee"];
const ACTIVE_ROLES = ["decideur", "receptionnaire", "lecteur"];

export default function AviDetailModal({ aviId, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usersById, setUsersById] = useState({});
  const [decisionOpen, setDecisionOpen] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [remindTarget, setRemindTarget] = useState(null);
  const [bottomTab, setBottomTab] = useState("chat");

  // Fermeture Escape (mais seulement si aucune sous-modale n'est ouverte).
  useEscapeClose(() => {
    if (decisionOpen || cancelOpen || editOpen || remindTarget || taskOpen) return;
    onClose?.();
  });

  // Spec iter79 fix (Feb 2026) — verrou de scroll du body tant que la
  // modale est ouverte. Restauré à l'unmount pour ne pas geler l'app.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const load = async () => {
    try {
      const r = await api.get(`/avi/${aviId}`);
      setDoc(r.data);
    } catch (e) {
      toast.error("Demande introuvable", { description: e?.response?.data?.detail || "" });
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/users/directory")
      .then((r) => {
        const items = r.data.items || r.data || [];
        setUsersById(items.reduce((acc, u) => { acc[u.id] = u; return acc; }, {}));
      })
      .catch(() => {});
  }, []);

  // Spec iter156 — ``load`` change à chaque render, on ne veut le
  // relancer que quand ``aviId`` change (ouverture d'une nouvelle fiche).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setLoading(true); load(); }, [aviId]);
  // iter372 pt 1 — ouverte → plus comptée dans la barre latérale.
  useEffect(() => { markSeen("avi", aviId); return () => { markSeen("avi", aviId); }; }, [aviId]);

  useEffect(() => {
    if (!aviId) return;
    api.post("/notifications/read-related", { avi_id: aviId }).catch(() => {});
  }, [aviId]);

  useAviStream({
    onAny: (evt) => { if (evt && evt.avi_id === aviId) load(); },
  });

  const meta = doc ? AVI_SUBTYPES[doc.avi_type] : null;
  const isAccuse = doc?.avi_type === "accuse";
  const isInfo = doc?.avi_type === "information";

  const {
    isCreator, isAdmin, myEntry, isDeciderMe, myPendingDecision,
    canEdit, canCancel, canRemind, canDecide,
    showDeciderActions, showRequesterActions,
    isFinal,
  } = useMemo(() => {
    if (!doc || !user) return {};
    const admin = hasRight(user, "avi.delete"); // iter200 — plus de rôle : droits de la matrice
    const creator = doc.created_by === user.id;
    const inScope = (user.rights?.avi?.scope || "own") === "all" || creator || !!user.is_first_admin;
    const mayEdit = inScope && hasRight(user, "avi.edit");
    const mine = (doc.participants || []).find((p) => p.user_id === user.id);
    // Spec iter144 Phase 3 (Feb 2026) — un « décideur/réceptionnaire »
    // moi = participant actif (rôle ∈ decideur, receptionnaire).
    const decMe = ACTIVE_ROLES.includes(mine?.role);
    const pendingMe = decMe && mine?.decision === null;
    const activeStatus = ["en_attente", "partiel_repondu"].includes(doc.status);
    const final = FINAL_STATUSES.includes(doc.status);
    return {
      isCreator: creator,
      isAdmin: admin,
      myEntry: mine,
      isDeciderMe: decMe,
      myPendingDecision: pendingMe,
      canEdit: mayEdit && doc.status === "en_attente",
      canCancel: (creator || admin) && activeStatus,
      canRemind: (creator || admin) && activeStatus,
      canDecide: pendingMe && activeStatus,
      showDeciderActions: pendingMe && activeStatus,
      showRequesterActions: (creator || admin) && activeStatus,
      isFinal: final,
    };
  }, [doc, user]);

  // Backdrop click ferme la modale (mais pas si sous-modale ouverte).
  const onBackdropClick = (e) => {
    // Seul un clic DIRECT sur le fond ferme : les clics des sous-modales (portails React, ex. Nouvelle tâche) remontent l'arbre React.
    if (e.target !== e.currentTarget) return;
    if (decisionOpen || cancelOpen || editOpen || remindTarget || taskOpen) return;
    onClose?.();
  };

  const submitDecision = async (decision, { reason, comment_type }) => {
    try {
      // Spec iter144/iter145 — endpoint polymorphe :
      //   Validation  → /approve | /refuse
      //   Accusé      → /acknowledge (Refuser retiré en iter145)
      //   Information → /mark-read
      let endpoint;
      if (decision === "accusee") endpoint = "acknowledge";
      else if (decision === "lue") endpoint = "mark-read";
      else if (decision === "approuvee") endpoint = "approve";
      else endpoint = "refuse";
      await api.post(`/avi/${doc.id}/${endpoint}`, { decision, reason: reason || null, comment_type: comment_type || null });
      const okMsg = decision === "approuvee" ? "Approuvée"
                  : decision === "accusee"   ? "Reçu"
                  : decision === "lue"       ? "Marquée comme lue"
                  : "Refusée";
      toast.success(okMsg);
      setDecisionOpen(null);
      await load();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    }
  };

  const submitCancel = async ({ reason }) => {
    try {
      await api.post(`/avi/${doc.id}/cancel`, { reason });
      toast.success("Demande annulée");
      setCancelOpen(false);
      await load();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    }
  };

  const submitRemind = async (targetUserId, note) => {
    try {
      await api.post(`/avi/${doc.id}/remind`, { target_user_id: targetUserId, note: note || null });
      toast.success("Relance envoyée");
      setRemindTarget(null);
      await load();
    } catch (e) {
      toast.error("Échec", { description: e?.response?.data?.detail || "" });
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4 sm:p-6"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
      data-testid="avi-detail-modal"
    >
      <div
        className="relative w-full rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: "#131313",
          border: `2px solid ${meta?.color || "#2A2A2A"}`,
          maxWidth: 1100,
          maxHeight: "92vh",
          boxShadow: "0 24px 80px rgba(0,0,0,0.55)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ===== Header modal ===== */}
        <div
          className="flex items-center justify-end gap-3 px-4 pt-3 shrink-0"
        >
          <span className="sr-only" data-testid="avi-detail-reference">{doc?.reference || "…"}</span>
          <div className="flex items-center gap-2 shrink-0">
            {isFinal && (
              <span
                className="inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10px] font-semibold"
                style={{ background: "rgba(156,163,175,0.14)", border: "1px solid rgba(156,163,175,0.38)", color: "#D1D5DB" }}
                title="Décision finale — cette demande est verrouillée en lecture seule"
                data-testid="avi-detail-readonly-badge"
              >
                <Lock size={11} /> Lecture seule
              </span>
            )}
            <DeskCloseX onClick={onClose} testId="avi-detail-close" />
          </div>
        </div>

        {/* ===== Body (scrollable) ===== */}
        <div
          className="flex-1 overflow-y-auto px-5 pb-5 pt-2"
          data-testid="avi-detail-page"
        >
          {(loading || !doc || !meta) ? (
            <div className="p-6 text-muted-foreground text-sm" data-testid="avi-detail-loading">Chargement…</div>
          ) : (
            <AviDetailBody
              doc={doc}
              meta={meta}
              user={user}
              usersById={usersById}
              status={(() => {
                const base = STATUS_META[doc.status] || {};
                const ovr = AVI_STATUS_OVERRIDES[doc.avi_type]?.[doc.status];
                return ovr ? { ...base, ...ovr } : base;
              })()}
              priority={PRIORITY_META[doc.priority] || {}}
              isFinal={isFinal}
              myPendingDecision={myPendingDecision}
              canEdit={canEdit}
              canCancel={canCancel}
              canRemind={canRemind}
              showDeciderActions={showDeciderActions}
              showRequesterActions={showRequesterActions}
              isAccuse={isAccuse}
              isInfo={isInfo}
              bottomTab={bottomTab}
              setBottomTab={setBottomTab}
              onCreateTask={() => setTaskOpen(true)}
              onDecide={(decision, reason) => submitDecision(decision, { reason: reason || null, comment_type: null })}
              onEdit={() => setEditOpen(true)}
              onCancel={() => setCancelOpen(true)}
              onRemind={(userId) => setRemindTarget(userId)}
              onGotoUser={(id) => navigate(`/parametres?tab=users&highlight=${encodeURIComponent(id)}`)}
            />
          )}
        </div>
      </div>

      {/* ===== Sous-modales ===== */}
      {decisionOpen && (
        <AviDecisionModal
          decision={decisionOpen}
          onClose={() => setDecisionOpen(null)}
          onConfirm={(payload) => submitDecision(decisionOpen, payload)}
        />
      )}
      {taskOpen && doc && (
        <TaskCreateDialog open={taskOpen} onOpenChange={setTaskOpen} projects={[]} users={Object.values(usersById)}
          initialSubject={doc.subject || ""} initialMessage={doc.description || ""} sourceAviId={doc.id}
          onCreated={(t) => { setTaskOpen(false); toast.success(`Tâche ${t?.reference || ""} créée depuis cette fiche`); load?.(); }} />
      )}
      {cancelOpen && (
        <AviReasonModal
          title="Annuler la demande"
          description="Cette demande sera clôturée et notifiée à tous les participants."
          confirmLabel="Confirmer l'annulation"
          confirmColor="#9CA3AF"
          onClose={() => setCancelOpen(false)}
          onConfirm={submitCancel}
        />
      )}
      {remindTarget && (
        <AviReminderModal
          user={usersById[remindTarget]}
          onClose={() => setRemindTarget(null)}
          onConfirm={(note) => submitRemind(remindTarget, note)}
        />
      )}
      {editOpen && (
        <AviQuickEditModal
          avi={doc}
          onClose={() => setEditOpen(false)}
          onSaved={async () => { setEditOpen(false); await load(); }}
        />
      )}
    </div>,
    document.body
  );
}
